import React, { useRef } from 'react';
import { Globe, Mail, MessageCircle, Phone, UserRound, X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogPortal, DialogTitle } from '../ui/dialog';
import type { ContactProfile } from '../../types/contactProfile';
import { contactLink, publicContactProfile } from '../../config/contactProfile.mjs';

const displayUrl = (value: string) => value.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '');

export function visibleContactProfile(value?: ContactProfile | null) {
  const profile = publicContactProfile(value ? { ...value, photo: null } : null);
  if (!profile) return null;
  // 공개 응답에는 Storage 경로가 없으므로 사진 URL만 별도로 허용한다.
  const url = value?.photo?.url;
  if (url && /^\/\.netlify\/functions\/workspace-media\?accountCode=[0-9A-F]{2}&asset=system-settings%2F[A-Za-z0-9_-]+\.(png|jpg|jpeg|webp)$/i.test(url)) profile.photo = { url };
  return profile;
}

export default function ConsultantContactDialog({ open, onOpenChange, profile, returnFocus }: {
  open: boolean; onOpenChange: (open: boolean) => void; profile: NonNullable<ReturnType<typeof visibleContactProfile>>;
  returnFocus: React.RefObject<HTMLButtonElement | null>;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const interactedOutside = useRef(false);
  const website = contactLink(profile.website);
  return <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
    {open && <DialogPortal><div className="promotion-theme promotion-contact-backdrop" aria-hidden="true" onClick={() => onOpenChange(false)} /></DialogPortal>}
    <DialogContent effectSound={false} showCloseButton={false} className="promotion-theme promotion-contact-dialog" aria-modal="true"
      onInteractOutside={() => { interactedOutside.current = true; }}
      onOpenAutoFocus={event => { event.preventDefault(); interactedOutside.current = false; closeRef.current?.focus({ preventScroll: true }); }}
      onCloseAutoFocus={event => { event.preventDefault(); if (!interactedOutside.current) returnFocus.current?.focus({ preventScroll: true }); }}>
      <div className="promotion-contact-dialog-header">
        <DialogTitle>상담원 연락처</DialogTitle>
        <DialogClose asChild><button ref={closeRef} type="button" aria-label="상담원 연락처 닫기"><X size={20} aria-hidden="true" /></button></DialogClose>
      </div>
      <DialogDescription className="sr-only">상담원의 연락처를 확인하세요.</DialogDescription>
      <div className="promotion-contact-dialog-body">
        <div className="promotion-consultant-heading">
          {profile.photo ? <img src={profile.photo.url} alt="담당자 사진" /> : <UserRound size={56} aria-hidden="true" />}
          <strong>{profile.name}</strong>
        </div>
        {profile.introduction?.trim() && <p className="promotion-consultant-introduction">{profile.introduction}</p>}
        <ul className="promotion-consultant-links">{profile.contacts.map((item, index) => {
          const link = contactLink(item.value);
          const Icon = link.kind === 'phone' ? Phone : link.kind === 'email' ? Mail : MessageCircle;
          return <li key={index}><Icon size={24} aria-hidden="true" /><div><span>{item.label}</span>
            {link.href ? <a href={link.href} {...(link.kind === 'web' ? { target: '_blank', rel: 'noopener noreferrer', className: 'promotion-consultant-url' } : {})}>{link.kind === 'web' ? displayUrl(item.value) : item.value}</a> : <p>{item.value}</p>}
          </div></li>;
        })}
          {profile.website && <li><Globe size={24} aria-hidden="true" /><div><span>대표 웹사이트</span>
            {website.kind === 'web' && website.href ? <a href={website.href} target="_blank" rel="noopener noreferrer" className="promotion-consultant-url">{displayUrl(profile.website)}</a> : <p>{profile.website}</p>}
          </div></li>}
        </ul>
      </div>
    </DialogContent>
  </Dialog>;
}
